/**
 * StepLines — the shared "steps" floor visualization used by every editor: a
 * group of perpendicular snap lines at fixed beat positions in the scene that
 * scroll forward/back as the playhead moves (so they read as anchored to the
 * track, passing the trigger, rather than a window that always sits ahead of it).
 *
 * Each line's position AND opacity are updated per frame from its continuous
 * distance to the trigger, so new lines fade IN smoothly at the far edge (no
 * pop-in) and fade OUT as they pass behind.
 *
 * Scene-agnostic: each editor passes its own `beatToZ(beat, currentBeat)` (which
 * bakes in that scene's strike position, direction and NJS/beat spacing), plus
 * the track width and the live step / current-beat state.
 */

import React, { useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Line } from '@react-three/drei';

// drei's <Line> ref is a Line2 (Object3D with a LineMaterial). Typed loosely so
// we can set .position.z and .material.opacity each frame without extra deps.
type LineRef = { position: { z: number }; material: { opacity: number } };

interface StepLinesProps {
  /** Full track width — each line spans -width/2+pad … width/2-pad. */
  width: number;
  /** Beats per step (the editor's scroll/snap step). */
  step: number;
  /** Maps an absolute beat (+ the live current beat) to a world Z in the scene. */
  beatToZ: (beat: number, currentBeat: number) => number;
  getCurrentBeat: () => number;
  /** Fades the whole group OUT over ~1s when play starts, and back IN when it stops. */
  playing?: boolean;
  color: string;
  lineWidth?: number;
  opacity?: number;
  /** Floor height of the lines. */
  y?: number;
  /** How many steps to keep ahead of / behind the trigger in the scroll window. */
  stepsAhead?: number;
  stepsBehind?: number;
  /** Alternative to steps*: keep a CONSTANT distance in BEATS regardless of the
   *  step division (reviewer ask: the snap blanket shouldn't shrink to nothing
   *  at coarse snaps or stretch forever at fine ones). Wins over steps* when
   *  set. Line count is capped, so ultra-fine snaps cover what the cap allows. */
  beatsAhead?: number;
  beatsBehind?: number;
  /** Horizontal inset from each edge. */
  pad?: number;
  /** When false, the lines stay fully visible during playback instead of
   *  easing out (mappers tracking timing want the grid while the music plays). */
  fadeWhilePlaying?: boolean;
}

export default function StepLines({
  width, step, beatToZ, getCurrentBeat, playing = false, fadeWhilePlaying = true,
  color, lineWidth = 1, opacity = 1, y = 0.005, pad = 0.05,
  beatsAhead, beatsBehind,
  ...stepCounts
}: StepLinesProps) {
  // Beat-coverage mode: derive the step counts from a fixed beat distance so
  // the blanket length doesn't change with the snap division. Each line is a
  // draw call, so the count is capped — at very fine snaps the cap wins.
  const stepSafe = Math.max(0.0001, step);
  const stepsAhead = beatsAhead != null
    ? Math.min(48, Math.max(1, Math.round(beatsAhead / stepSafe)))
    : (stepCounts.stepsAhead ?? 12);
  const stepsBehind = beatsBehind != null
    ? Math.min(12, Math.max(1, Math.round(beatsBehind / stepSafe)))
    : (stepCounts.stepsBehind ?? 4);
  const hw = width / 2;
  // Each rendered step keeps its own line, keyed by its ABSOLUTE step index, so
  // a line never teleports — it scrolls (and fades) until it leaves the window.
  const refs = useRef<Map<number, LineRef>>(new Map());
  const centerRef = useRef(0);
  const [stepCenter, setStepCenter] = useState(0);
  // Global play fade: 1 = fully shown, 0 = hidden. Ramps linearly over 1s.
  const playFadeRef = useRef(1);

  useFrame((_, delta) => {
    // Ease the whole group out (play) / in (stop) over ~1 second.
    const dir = (playing && fadeWhilePlaying) ? -1 : 1;
    playFadeRef.current = Math.max(0, Math.min(1, playFadeRef.current + dir * (delta / 1.0)));
    const playFade = playFadeRef.current;

    const cb = getCurrentBeat();
    const s = Math.max(0.0001, step);
    const sc = Math.round(cb / s);
    if (sc !== centerRef.current) { centerRef.current = sc; setStepCenter(sc); }

    const maxAheadBeats = stepsAhead * s;
    const fadeInBeats  = Math.max(s, maxAheadBeats * 0.35); // ramp in over the far third
    const fadeOutBeats = Math.max(s, stepsBehind * s);      // ramp out behind the trigger

    refs.current.forEach((line, k) => {
      if (!line) return;
      const aheadBeats = k * s - cb;                 // >0 ahead of trigger, <0 behind
      line.position.z = beatToZ(k * step, cb);
      // Fade in from the far edge; fade out once it slips behind the trigger.
      const inF  = Math.min(1, Math.max(0, (maxAheadBeats - aheadBeats) / fadeInBeats));
      const outF = aheadBeats >= 0 ? 1 : Math.max(0, 1 + aheadBeats / fadeOutBeats);
      line.material.opacity = opacity * Math.min(inF, outF) * playFade;
    });
  });

  const ks: number[] = [];
  for (let k = stepCenter - stepsBehind; k <= stepCenter + stepsAhead; k++) {
    if (k >= 0) ks.push(k);
  }

  // ONE stable points array for every line. drei's <Line> rebuilds (and
  // re-uploads) its LineGeometry whenever the `points` prop changes identity —
  // an inline array literal here meant five GPU buffer uploads per line on
  // every render of this component, which showed up as a hard frame-rate hit
  // on dense charts. The lines are identical; only their Z and opacity move
  // (imperatively, in useFrame above).
  const points = useMemo<[number, number, number][]>(
    () => [[-hw + pad, y, 0], [hw - pad, y, 0]],
    [hw, pad, y],
  );

  return (
    <>
      {ks.map(k => (
        <Line
          key={k}
          ref={(el: unknown) => { if (el) refs.current.set(k, el as LineRef); else refs.current.delete(k); }}
          points={points}
          color={color}
          lineWidth={lineWidth}
          transparent
          opacity={0}
        />
      ))}
    </>
  );
}
